import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, LogOut, Upload, UserRound, Users } from 'lucide-react';
import { VideoList } from '../components/VideoList';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b-2 border-foreground bg-primary text-primary-foreground">
        <div className="mx-auto flex min-h-14 max-w-7xl flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="shrink-0 text-left text-lg font-black tracking-tight text-primary-foreground"
          >
            <span className="bg-foreground text-primary px-2 py-0.5 font-mono mr-1.5">ABR</span>
            streaming
          </button>

          <div className="flex flex-wrap items-center gap-2">
            {user ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate('/rooms')}>
                  <Users className="w-4 h-4" />
                  Rooms
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate('/upload')}>
                  <Upload className="w-4 h-4" />
                  Upload
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate('/account')}>
                  <UserRound className="w-4 h-4" />
                  Account
                </Button>
                <Button variant="ghost" size="sm" onClick={logout}>
                  <LogOut className="w-4 h-4" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
                  <LogIn className="w-4 h-4" />
                  Sign in
                </Button>
                <Button size="sm" onClick={() => navigate('/register')} className="bg-background text-foreground">
                  Register
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="border-b-2 border-foreground stripe-bg">
        <div className="mx-auto max-w-7xl px-3 py-8 text-center sm:px-6 sm:py-14">
          <h1 className="mb-4 text-3xl font-black leading-tight tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Adaptive streaming video
          </h1>
          <p className="text-foreground/60 text-base md:text-lg max-w-xl mx-auto mb-8 font-medium">
            Upload videos, transcode them to HLS, and watch with adaptive bitrate controls.
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
            <Button onClick={() => navigate(user ? '/upload' : '/login')} size="lg" className="w-full font-black text-base sm:w-auto">
              <Upload className="w-5 h-5" />
              Upload video
            </Button>
            {user && (
              <Button onClick={() => navigate('/rooms')} size="lg" variant="secondary" className="w-full font-black text-base sm:w-auto">
                <Users className="w-5 h-5" />
                Co-watch
              </Button>
            )}
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-3 py-6 sm:px-6 sm:py-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h2 className="border-b-4 border-primary pb-1 text-xl font-black text-foreground sm:text-2xl">
            Videos
          </h2>
          <span className="text-foreground/60 text-xs font-mono font-bold border-2 border-foreground px-2.5 py-1 bg-card shadow-neo-sm">
            HLS / ABR
          </span>
        </div>
        <VideoList />
      </main>
    </div>
  );
};
